import React, { useState, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { createPortal } from 'react-dom';
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';
import { isNodeTypeSelected } from '@/lib/tiptap-utils';
import { NodeSelection } from '@tiptap/pm/state';
import { ImageNodeFloating } from '@/components/tiptap-node/image-node/image-node-floating';
import { DeleteNodeButton } from '@/components/tiptap-ui/delete-node-button';
import { ImageDownloadButton } from '@/components/tiptap-ui/image-download-button';
import { ImageCaptionButton, useImageCaption, setMediaCaption } from '@/components/tiptap-ui/image-caption-button';
import { ImageUploadButton } from '@/components/tiptap-ui/image-upload-button';
import { useImageDownload, downloadSelectedMedia } from '@/components/tiptap-ui/image-download-button';
import { ImageCaptionIcon } from '@/components/tiptap-icons/image-caption-icon';
import { Separator } from '@/components/tiptap-ui-primitive/separator';
import { Button } from '@/components/tiptap-ui-primitive/button';
import { RefreshCcwIcon } from '@/components/tiptap-icons/refresh-ccw-icon';
import { ArrowDownToLineIcon } from '@/components/tiptap-icons/arrow-down-to-line-icon';
import { LinkIcon } from '@/components/tiptap-icons/link-icon';
import { TagIcon } from '@/components/tiptap-icons/tag-icon';
import { TrashIcon } from '@/components/tiptap-icons/trash-icon';
import { PlusIcon } from '@/components/tiptap-icons/plus-icon';
import {
  findResourceRowDepth,
  resourceRowHasSubtitle,
} from '../lib/resource_row_paragraph.js';
import { useFloating, autoUpdate, offset, flip, shift } from '@floating-ui/react';
import { HIDE_FLOATING_META } from '@/hooks/use-floating-toolbar-visibility';
import { uploadToAzure } from '../direct_uploads/tiptap_direct_upload.js';
import { propagateCarouselSlideMediaToSharedResourceIds } from '../lib/carousel_slide_from_resource.js';
import {
  POST_EDITOR_FLOATING_MENU_KINDS,
  postEditorFloatingContainerClasses,
  postEditorFloatingKindFromRowType,
  postEditorFloatingToolbarGroupClasses,
  postEditorFloatingPositionStyle,
  POST_EDITOR_BUBBLE_MENU_OFFSET,
  POST_EDITOR_BUBBLE_MENU_PLACEMENT,
  postEditorBubbleMenuFloatingStyles,
  postEditorImageFloatingReference,
  postEditorResourceRowFloatingReference,
  postEditorFloatingToolbarProps,
  postEditorVideoFixedMenuClasses,
} from '../lib/post_editor_constants.js';

const ROW_RESOURCE_NODE_TYPES = ['downloadRow', 'externalLinkRow', 'meetingRow', 'audioRow'];

/** Root-doc positions ProseMirror accepts for resolve/node probes (posAtDOM returns -1 when DOM is stale/outside doc). */
function isValidDocRootPosition(editor, pos) {
  if (!editor?.state?.doc || typeof pos !== 'number' || Number.isNaN(pos)) return false;
  const max = editor.state.doc.content.size;
  return pos >= 0 && pos <= max;
}

function getEditorViewDom(editor) {
  if (!editor || editor.isDestroyed) return null;
  try {
    return editor.view?.dom ?? null;
  } catch {
    return null;
  }
}

function posAtDOMSafe(editor, node, offset = 0) {
  if (!editor || editor.isDestroyed || !node) return null;
  try {
    const view = editor.view;
    if (!view) return null;
    const p = view.posAtDOM(node, offset);
    return isValidDocRootPosition(editor, p) ? p : null;
  } catch {
    return null;
  }
}

function rowEditorDomMatchesTr(editor, tr, nodePos) {
  if (!isValidDocRootPosition(editor, nodePos)) return false;
  let nodeDOM;
  try {
    nodeDOM = editor.view.nodeDOM(nodePos);
  } catch {
    return false;
  }
  return !!(
    nodeDOM &&
    (nodeDOM === tr ||
      tr.contains(nodeDOM) ||
      (nodeDOM instanceof HTMLElement && nodeDOM.closest('tr[data-resource-row]') === tr))
  );
}

/** Prefer `data-resource-id` on the row, then find the matching row node in the doc (posAtDOM often lands inside the row). */
function resolveResourceIdForResourceRow(editor, tr) {
  if (!editor || !tr || !(tr instanceof HTMLElement)) return null;
  const fromDom = tr.getAttribute('data-resource-id') || tr.dataset?.resourceId;
  if (fromDom) return String(fromDom);

  let rowPos = posAtDOMSafe(editor, tr, 0);
  if (rowPos == null) {
    for (let pos = 0; pos < editor.state.doc.content.size; pos++) {
      const node = editor.state.doc.nodeAt(pos);
      if (node && ROW_RESOURCE_NODE_TYPES.includes(node.type.name) && rowEditorDomMatchesTr(editor, tr, pos)) {
        rowPos = pos;
        break;
      }
    }
  }
  if (rowPos == null) return null;

  let rowNode = null;
  const direct = editor.state.doc.nodeAt(rowPos);
  if (direct && ROW_RESOURCE_NODE_TYPES.includes(direct.type.name) && rowEditorDomMatchesTr(editor, tr, rowPos)) {
    rowNode = direct;
  } else {
    for (let checkPos = rowPos; checkPos >= 0; checkPos--) {
      const checkNode = editor.state.doc.nodeAt(checkPos);
      if (checkNode && ROW_RESOURCE_NODE_TYPES.includes(checkNode.type.name) && rowEditorDomMatchesTr(editor, tr, checkPos)) {
        rowNode = checkNode;
        break;
      }
    }
    if (!rowNode) {
      for (let checkPos = rowPos; checkPos < editor.state.doc.content.size; checkPos++) {
        const checkNode = editor.state.doc.nodeAt(checkPos);
        if (checkNode && ROW_RESOURCE_NODE_TYPES.includes(checkNode.type.name) && rowEditorDomMatchesTr(editor, tr, checkPos)) {
          rowNode = checkNode;
          break;
        }
      }
    }
  }

  const fromDoc = rowNode?.attrs?.resourceId;
  return fromDoc ? String(fromDoc) : null;
}

/** Find the document position for a given resource row `<tr>`. */
function resolveRowPosForResourceTr(editor, tr, preferredType) {
  if (!editor || !tr || !(tr instanceof HTMLElement)) return null;

  let rowPos = posAtDOMSafe(editor, tr, 0);

  const matchesType = (node) =>
    !!(node && ROW_RESOURCE_NODE_TYPES.includes(node.type.name) && (!preferredType || node.type.name === preferredType));

  const domMatches = (pos) => rowEditorDomMatchesTr(editor, tr, pos);

  if (rowPos != null) {
    const direct = editor.state.doc.nodeAt(rowPos);
    if (matchesType(direct) && domMatches(rowPos)) return rowPos;

    for (let checkPos = rowPos; checkPos >= 0; checkPos--) {
      const n = editor.state.doc.nodeAt(checkPos);
      if (matchesType(n) && domMatches(checkPos)) return checkPos;
    }
    for (let checkPos = rowPos; checkPos < editor.state.doc.content.size; checkPos++) {
      const n = editor.state.doc.nodeAt(checkPos);
      if (matchesType(n) && domMatches(checkPos)) return checkPos;
    }
  }

  for (let pos = 0; pos < editor.state.doc.content.size; pos++) {
    const n = editor.state.doc.nodeAt(pos);
    if (matchesType(n) && domMatches(pos)) return pos;
  }

  return null;
}

/**
 * Resolve resource id from the editor document (selection / cursor), optionally
 * constrained to a row type that matches the floating toolbar. Prefer this over
 * DOM-only resolution so adjacent resource rows never pick up the wrong tr.
 */
function resolveResourceIdForToolbarRow(editor, preferredRowType) {
  if (!editor) return null;
  const { selection } = editor.state;

  const ridFromNode = (node) => {
    const rid = node?.attrs?.resourceId;
    return rid != null && rid !== '' ? String(rid) : null;
  };

  if (selection instanceof NodeSelection) {
    const node = selection.node;
    if (node && ROW_RESOURCE_NODE_TYPES.includes(node.type.name)) {
      if (!preferredRowType || node.type.name === preferredRowType) {
        const r = ridFromNode(node);
        if (r) return r;
      }
    }
  }

  const $from = selection.$from;
  for (let d = $from.depth; d >= 0; d -= 1) {
    const n = $from.node(d);
    if (!n || !ROW_RESOURCE_NODE_TYPES.includes(n.type.name)) continue;
    if (preferredRowType && n.type.name !== preferredRowType) continue;
    const r = ridFromNode(n);
    if (r) return r;
  }

  return null;
}

function resolveResourceIdForVideoPos(editor, pos) {
  if (!editor || typeof pos !== 'number') return null;
  const fromAttrs = editor.state.doc.nodeAt(pos)?.attrs?.resourceId;
  if (fromAttrs) return String(fromAttrs);
  try {
    const el = editor.view.nodeDOM(pos);
    const withId = el?.closest?.('[data-resource-id]');
    const id = withId?.getAttribute('data-resource-id');
    return id ? String(id) : null;
  } catch {
    return null;
  }
}

/** Toolbar image icon (same as Insert Image/Video in TiptapToolbar) for Upload thumbnail */
const UploadThumbnailIcon = ({ className, style } = {}) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    style={style}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M20 2C20 1.44772 19.5523 1 19 1C18.4477 1 18 1.44772 18 2V4H16C15.4477 4 15 4.44772 15 5C15 5.55228 15.4477 6 16 6H18V8C18 8.55228 18.4477 9 19 9C19.5523 9 20 8.55228 20 8V6H22C22.5523 6 23 5.55228 23 5C23 4.44772 22.5523 4 22 4H20V2ZM5 4C4.73478 4 4.48043 4.10536 4.29289 4.29289C4.10536 4.48043 4 4.73478 4 5V19C4 19.2652 4.10536 19.5196 4.29289 19.7071C4.48043 19.8946 4.73478 20 5 20H5.58579L14.379 11.2068C14.9416 10.6444 15.7045 10.3284 16.5 10.3284C17.2955 10.3284 18.0584 10.6444 18.621 11.2068L20 12.5858V12C20 11.4477 20.4477 11 21 11C21.5523 11 22 11.4477 22 12V14.998C22 14.9994 22 15.0007 22 15.002V19C22 19.7957 21.6839 20.5587 21.1213 21.1213C20.5587 21.6839 19.7957 22 19 22H6.00219C6.00073 22 5.99927 22 5.99781 22H5C4.20435 22 3.44129 21.6839 2.87868 21.1213C2.31607 20.5587 2 19.7957 2 19V5C2 4.20435 2.31607 3.44129 2.87868 2.87868C3.44129 2.31607 4.20435 2 5 2H12C12.5523 2 13 2.44772 13 3C13 3.55228 12.5523 4 12 4H5ZM8.41422 20H19C19.2652 20 19.5196 19.8946 19.7071 19.7071C19.8946 19.5196 20 19.2652 20 19V15.4142L17.207 12.6212C17.0195 12.4338 16.7651 12.3284 16.5 12.3284C16.2349 12.3284 15.9806 12.4337 15.7931 12.6211L8.41422 20ZM6.87868 6.87868C7.44129 6.31607 8.20435 6 9 6C9.79565 6 10.5587 6.31607 11.1213 6.87868C11.6839 7.44129 12 8.20435 12 9C12 9.79565 11.6839 10.5587 11.1213 11.1213C10.5587 11.6839 9.79565 12 9 12C8.20435 12 7.44129 11.6839 6.87868 11.1213C6.31607 10.5587 6 9.79565 6 9C6 8.20435 6.31607 7.44129 6.87868 6.87868ZM9 8C8.73478 8 8.48043 8.10536 8.29289 8.29289C8.10536 8.48043 8 8.73478 8 9C8 9.26522 8.10536 9.51957 8.29289 9.70711C8.48043 9.89464 8.73478 10 9 10C9.26522 10 9.51957 9.89464 9.70711 9.70711C9.89464 9.51957 10 9.26522 10 9C10 8.73478 9.89464 8.48043 9.70711 8.29289C9.51957 8.10536 9.26522 8 9 8Z"
      fill="currentColor"
    />
  </svg>
);

/**
 * MediaNodeFloating - Floating menu for images and videos
 * Shows a floating toolbar when an image or video node is selected
 */
export const MediaNodeFloating = ({ editor: providedEditor }) => {
  const { editor } = useTiptapEditor(providedEditor);
  const floatingRef = useRef(null);
  const hideTimeoutRef = useRef(null);
  const rowPositionRef = useRef(new WeakMap()); // Track row positions to avoid re-setting
  const activeRowTrRef = useRef(null); // Last interacted/selected resource row <tr>
  const [referenceElement, setReferenceElement] = useState(null);
  const [shouldHide, setShouldHide] = useState(false);
  const [videoContainers, setVideoContainers] = useState([]); // Track all video containers
  const [hoveredRowElement, setHoveredRowElement] = useState(null);
  const [hoveredRowType, setHoveredRowType] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const thumbnailFileInputRef = useRef(null);
  const pendingThumbnailResourceIdRef = useRef(null);
  const [thumbnailUploadingResourceId, setThumbnailUploadingResourceId] = useState(null);
  
  // Check if image or video is selected
  const isImageSelected = isNodeTypeSelected(editor, ['image']);
  const isVideoSelected = isNodeTypeSelected(editor, ['video']);
  const isDownloadRowSelected = isNodeTypeSelected(editor, ['downloadRow']);
  const isExternalLinkRowSelected = isNodeTypeSelected(editor, ['externalLinkRow']);
  const isMeetingRowSelected = isNodeTypeSelected(editor, ['meetingRow']);
  const isAudioRowSelected = isNodeTypeSelected(editor, ['audioRow']);

  // Listen for transactions that hide the floating toolbar (e.g., when drag handle is clicked)
  const hideRef = useRef(false);

  useEffect(() => {
    if (!editor) return;

    const handleTransaction = ({ transaction }) => {
      if (transaction.getMeta(HIDE_FLOATING_META)) {
        hideRef.current = true;
      }
    };

    editor.on('transaction', handleTransaction);
    return () => {
      editor.off('transaction', handleTransaction);
    };
  }, [editor]);

  // Handle re-click on same node (should show floating menu again)
  useEffect(() => {
    if (!editor) return;
    const dom = getEditorViewDom(editor);
    if (!dom) return;

    const onPointerDown = (e) => {
      // Don't interfere with clicks on the floating menu itself
      if (floatingRef.current && floatingRef.current.contains(e.target)) {
        return;
      }
      
      const sel = editor.state.selection;
      if (!(sel instanceof NodeSelection)) return;
      const nodeDom = editor.view.nodeDOM(sel.from);
      if (!nodeDom) return;
      if (nodeDom.contains(e.target)) {
        hideRef.current = false;
        setShouldHide(false);
      }
    };

    dom.addEventListener('pointerdown', onPointerDown, { capture: true });
    return () => {
      dom.removeEventListener('pointerdown', onPointerDown, { capture: true });
    };
  }, [editor]);

  // Update shouldHide based on hideRef
  useEffect(() => {
    setShouldHide(hideRef.current);
  }, [isImageSelected, isVideoSelected, isDownloadRowSelected, isExternalLinkRowSelected, isMeetingRowSelected, isAudioRowSelected]);

  // Clear hover state when row is selected
  useEffect(() => {
    if (isDownloadRowSelected || isExternalLinkRowSelected || isMeetingRowSelected || isAudioRowSelected) {
      setHoveredRowElement(null);
      setHoveredRowType(null);
    }
  }, [isDownloadRowSelected, isExternalLinkRowSelected, isMeetingRowSelected, isAudioRowSelected]);

  // Listen for video button events from VideoExtension
  useEffect(() => {
    if (!editor) return;
    
    const handleVideoToggleCaption = (e) => {
      const { nodePos } = e.detail;
      if (typeof nodePos === 'number' && nodePos >= 0) {
        editor.chain().focus().setNodeSelection(nodePos).run();
        setTimeout(() => {
          setMediaCaption(editor);
        }, 10);
      }
    };
    
    const handleVideoDownload = async (e) => {
      const { nodePos } = e.detail;
      if (typeof nodePos === 'number' && nodePos >= 0) {
        editor.chain().focus().setNodeSelection(nodePos).run();
        setTimeout(async () => {
          await downloadSelectedMedia(editor);
        }, 10);
      }
    };
    
    document.addEventListener('tiptap:video:toggleCaption', handleVideoToggleCaption);
    document.addEventListener('tiptap:video:download', handleVideoDownload);
    
    return () => {
      document.removeEventListener('tiptap:video:toggleCaption', handleVideoToggleCaption);
      document.removeEventListener('tiptap:video:download', handleVideoDownload);
    };
  }, [editor]);

  // Upload thumbnail: trigger file input when event is dispatched (e.g. from VideoExtension button)
  const startThumbnailUpload = (resourceId) => {
    if (!resourceId) return;
    pendingThumbnailResourceIdRef.current = resourceId;
    thumbnailFileInputRef.current?.click();
  };

  const handleThumbnailFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const resourceId = pendingThumbnailResourceIdRef.current;
    pendingThumbnailResourceIdRef.current = null;
    if (!file || !resourceId) return;
    if (!file.type.startsWith('image/')) {
      console.warn('[MediaNodeFloating] Thumbnail must be an image');
      return;
    }
    setThumbnailUploadingResourceId(resourceId);
    try {
      const { signed_id: signedId } = await uploadToAzure(file);
      const csrfToken = document.querySelector('meta[name=csrf-token]')?.getAttribute('content') || '';
      const res = await fetch(`/resources/${resourceId}/update_thumbnail`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ resource: { display_image_file_signed_id: signedId } }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        console.error('[MediaNodeFloating] Thumbnail update failed:', data.error || res.statusText);
        return;
      }
      // Update every standalone video and every carousel slide that uses this resource.
      if (data.display_image_url && editor) {
        const posterUrl = data.display_image_url;
        const resourceIdStr = String(resourceId);
        editor.commands.command(({ tr, dispatch, state }) => {
          if (!dispatch) return false;
          const ops = [];
          state.doc.descendants((node, pos) => {
            if (node.type.name === 'video' && String(node.attrs.resourceId) === resourceIdStr) {
              ops.push({ pos, attrs: { ...node.attrs, poster: posterUrl } });
            }
            if (node.type.name === 'postImageCarousel' && Array.isArray(node.attrs.slides)) {
              const slides = node.attrs.slides;
              const hasSlide = slides.some(
                (s) => String(s?.resourceId) === resourceIdStr && s.type === 'video'
              );
              if (!hasSlide) return;
              const posterSignedId = data.display_image_file_signed_id
                ? String(data.display_image_file_signed_id)
                : null;
              const nextSlides = slides.map((s) => {
                if (String(s?.resourceId) === resourceIdStr && s.type === 'video') {
                  const updated = { ...s, poster: posterUrl };
                  if (posterSignedId) updated.posterSignedId = posterSignedId;
                  return updated;
                }
                return s;
              });
              const ref = nextSlides.find(
                (s) => String(s?.resourceId) === resourceIdStr && s.type === 'video'
              );
              const finalSlides = ref
                ? propagateCarouselSlideMediaToSharedResourceIds(nextSlides, {
                    ...ref,
                    poster: posterUrl,
                    ...(posterSignedId ? { posterSignedId } : {}),
                  })
                : nextSlides;
              ops.push({ pos, attrs: { ...node.attrs, slides: finalSlides } });
            }
          });
          if (ops.length === 0) return true;
          ops.sort((a, b) => b.pos - a.pos);
          ops.forEach(({ pos, attrs }) => {
            tr.setNodeMarkup(pos, undefined, attrs);
          });
          dispatch(tr);
          return true;
        });
      }
    } catch (err) {
      console.error('[MediaNodeFloating] Thumbnail upload failed:', err);
    } finally {
      setThumbnailUploadingResourceId(null);
    }
  };

  useEffect(() => {
    const handler = (e) => {
      const resourceId = e.detail?.resourceId;
      if (resourceId) startThumbnailUpload(resourceId);
    };
    document.addEventListener('tiptap:video:uploadThumbnail', handler);
    return () => document.removeEventListener('tiptap:video:uploadThumbnail', handler);
  }, []);

  // Find all video containers in the editor and manually attach menus
  // NOTE: Buttons are now created directly in VideoExtension, so this is mostly for backwards compatibility
  useEffect(() => {
    if (!editor) return;

    const editorDOM = getEditorViewDom(editor);
    if (!editorDOM) return;

    let attachTimeout = null;
    const findAndAttachMenus = () => {
      // Clear any pending attachment
      if (attachTimeout) {
        clearTimeout(attachTimeout);
        attachTimeout = null;
      }

      const dom = getEditorViewDom(editor);
      if (!dom) return;
      const containers = Array.from(dom.querySelectorAll('.video-extension-container'));
      
      containers.forEach((container, index) => {
        if (!(container instanceof HTMLElement)) return;
        
        // Find menu slot
        let menuSlot = container.querySelector('.tiptap-video-fixed-menu[data-menu-slot="true"]');
        if (!menuSlot) {
          return;
        }
        
        // Get the group element
        const group = menuSlot.querySelector('.tiptap-toolbar-group[data-menu-group="true"]');
        if (!group) {
          return;
        }

        // If toolbar has 4 buttons but no "Upload thumbnail", inject it (handles cached/old VideoExtension bundle)
        const existingButtons = Array.from(group.querySelectorAll('button'));
        const hasUploadThumbnail = existingButtons.some(b => (b.title || b.getAttribute('aria-label') || '') === 'Upload thumbnail');
        if (existingButtons.length >= 4 && !hasUploadThumbnail) {
          const videoEl = container.querySelector('video');
          let resourceId = null;
          if (editor && videoEl) {
            try {
              const videoPos = posAtDOMSafe(editor, videoEl, 0);
              if (videoPos != null) {
                for (let checkPos = videoPos; checkPos >= 0; checkPos--) {
                  const node = editor.state.doc.nodeAt(checkPos);
                  if (node?.type?.name === 'video') {
                    const nodeDOM = editor.view.nodeDOM(checkPos);
                    if (nodeDOM === container || (nodeDOM && container.contains(nodeDOM))) {
                      resourceId = node.attrs?.resourceId;
                      break;
                    }
                  }
                }
              }
            } catch (_) {}
          }
          const deleteBtn = existingButtons[existingButtons.length - 1];
          if (deleteBtn && resourceId) {
            const sep = document.createElement('div');
            sep.setAttribute('role', 'separator');
            sep.setAttribute('aria-orientation', 'vertical');
            sep.style.cssText = 'width: 1px; height: 16px; background-color: rgba(255, 255, 255, 0.3); margin: 0 2px;';
            const uploadBtn = document.createElement('button');
            uploadBtn.type = 'button';
            uploadBtn.setAttribute('data-style', 'ghost');
            uploadBtn.setAttribute('data-weight', 'small');
            uploadBtn.setAttribute('role', 'button');
            uploadBtn.setAttribute('aria-label', 'Upload thumbnail');
            uploadBtn.title = 'Upload thumbnail';
            uploadBtn.style.cssText = 'display: flex; align-items: center; justify-content: center; padding: 4px; border: none; background: transparent; cursor: pointer; color: white;';
            uploadBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M20 2C20 1.44772 19.5523 1 19 1C18.4477 1 18 1.44772 18 2V4H16C15.4477 4 15 4.44772 15 5C15 5.55228 15.4477 6 16 6H18V8C18 8.55228 18.4477 9 19 9C19.5523 9 20 8.55228 20 8V6H22C22.5523 6 23 5.55228 23 5C23 4.44772 22.5523 4 22 4H20V2ZM5 4C4.73478 4 4.48043 4.10536 4.29289 4.29289C4.10536 4.48043 4 4.73478 4 5V19C4 19.2652 4.10536 19.5196 4.29289 19.7071C4.48043 19.8946 4.73478 20 5 20H5.58579L14.379 11.2068C14.9416 10.6444 15.7045 10.3284 16.5 10.3284C17.2955 10.3284 18.0584 10.6444 18.621 11.2068L20 12.5858V12C20 11.4477 20.4477 11 21 11C21.5523 11 22 11.4477 22 12V14.998C22 14.9994 22 15.0007 22 15.002V19C22 19.7957 21.6839 20.5587 21.1213 21.1213C20.5587 21.6839 19.7957 22 19 22H6.00219C6.00073 22 5.99927 22 5.99781 22H5C4.20435 22 3.44129 21.6839 2.87868 21.1213C2.31607 20.5587 2 19.7957 2 19V5C2 4.20435 2.31607 3.44129 2.87868 2.87868C3.44129 2.31607 4.20435 2 5 2H12C12.5523 2 13 2.44772 13 3C13 3.55228 12.5523 4 12 4H5ZM8.41422 20H19C19.2652 20 19.5196 19.8946 19.7071 19.7071C19.8946 19.5196 20 19.2652 20 19V15.4142L17.207 12.6212C17.0195 12.4338 16.7651 12.3284 16.5 12.3284C16.2349 12.3284 15.9806 12.4337 15.7931 12.6211L8.41422 20ZM6.87868 6.87868C7.44129 6.31607 8.20435 6 9 6C9.79565 6 10.5587 6.31607 11.1213 6.87868C11.6839 7.44129 12 8.20435 12 9C12 9.79565 11.6839 10.5587 11.1213 11.1213C10.5587 11.6839 9.79565 12 9 12C8.20435 12 7.44129 11.6839 6.87868 11.1213C6.31607 10.5587 6 9.79565 6 9C6 8.20435 6.31607 7.44129 6.87868 6.87868ZM9 8C8.73478 8 8.48043 8.10536 8.29289 8.29289C8.10536 8.48043 8 8.73478 8 9C8 9.26522 8.10536 9.51957 8.29289 9.70711C8.48043 9.89464 8.73478 10 9 10C9.26522 10 9.51957 9.89464 9.70711 9.70711C9.89464 9.51957 10 9.26522 10 9C10 8.73478 9.89464 8.48043 9.70711 8.29289C9.51957 8.10536 9.26522 8 9 8Z" fill="currentColor"/></svg>';
            uploadBtn.onclick = (e) => {
              e.preventDefault();
              e.stopPropagation();
              startThumbnailUpload(String(resourceId));
            };
            group.insertBefore(sep, deleteBtn);
            group.insertBefore(uploadBtn, deleteBtn);
          }
          return;
        }

        // Cached VideoExtension without Tags: insert before Replace (matches current VideoExtension order)
        const buttonsAfterThumb = Array.from(group.querySelectorAll('button'));
        const hasTagsButton = buttonsAfterThumb.some(
          (b) => (b.title || b.getAttribute('aria-label') || '') === 'Tags'
        );
        if (buttonsAfterThumb.length > 0 && !hasTagsButton) {
          const replaceBtn = buttonsAfterThumb.find(
            (b) => (b.title || b.getAttribute('aria-label') || '') === 'Replace Video'
          );
          if (replaceBtn) {
            const videoEl = container.querySelector('video');
            let rid = null;
            if (editor && videoEl) {
              try {
                const videoPos = posAtDOMSafe(editor, videoEl, 0);
                if (videoPos != null) {
                  for (let checkPos = videoPos; checkPos >= 0; checkPos--) {
                    const node = editor.state.doc.nodeAt(checkPos);
                    if (node?.type?.name === 'video') {
                      const nodeDOM = editor.view.nodeDOM(checkPos);
                      if (nodeDOM === container || (nodeDOM && container.contains(nodeDOM))) {
                        rid = node.attrs?.resourceId;
                        break;
                      }
                    }
                  }
                }
              } catch (_) {}
            }
            const sep = document.createElement('div');
            sep.setAttribute('role', 'separator');
            sep.setAttribute('aria-orientation', 'vertical');
            sep.style.cssText =
              'width: 1px; height: 16px; background-color: rgba(255, 255, 255, 0.3); margin: 0 2px;';
            const tagsBtn = document.createElement('button');
            tagsBtn.type = 'button';
            tagsBtn.setAttribute('data-style', 'ghost');
            tagsBtn.setAttribute('data-weight', 'small');
            tagsBtn.setAttribute('role', 'button');
            tagsBtn.setAttribute('aria-label', 'Tags');
            tagsBtn.title = 'Tags';
            tagsBtn.style.cssText =
              'display: flex; align-items: center; justify-content: center; padding: 4px; border: none; background: transparent; cursor: pointer; color: white;';
            tagsBtn.innerHTML =
              '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M20.59 13.41 11 3.83A2 2 0 0 0 9.59 3H4a1 1 0 0 0-1 1v5.59A2 2 0 0 0 3.83 11l9.58 9.59a2 2 0 0 0 2.83 0l4.35-4.35a2 2 0 0 0 0-2.83ZM6.5 7.5A1 1 0 1 1 7.5 6.5 1 1 0 0 1 6.5 7.5Z"/></svg>';
            tagsBtn.onclick = (e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!rid) return;
              document.dispatchEvent(
                new CustomEvent('tiptap:resource:requestTags', {
                  bubbles: true,
                  cancelable: true,
                  detail: { resourceId: String(rid) },
                })
              );
            };
            group.insertBefore(sep, replaceBtn);
            group.insertBefore(tagsBtn, replaceBtn);
            return;
          }
        }
        
        // Check if buttons already exist (created by VideoExtension) - don't overwrite with React
        if (existingButtons.length > 0) {
          return;
        }
        
        // Check if React root already exists
        if (group._reactRoot) {
          return;
        }
        
        // Ensure container has position: relative
        if (window.getComputedStyle(container).position === 'static') {
          container.style.position = 'relative';
        }
        
        // Find video position
        const videoElement = container.querySelector('video');
        if (!videoElement) {
          return;
        }
        
        const videoPos = posAtDOMSafe(editor, videoElement, 0);
        if (videoPos == null) {
          return;
        }
        
        // Walk backwards to find video node
        let actualVideoPos = null;
        for (let checkPos = videoPos; checkPos >= 0; checkPos--) {
          try {
            const node = editor.state.doc.nodeAt(checkPos);
            if (node && node.type.name === 'video') {
              const nodeDOM = editor.view.nodeDOM(checkPos);
              if (nodeDOM === container || container.contains(nodeDOM)) {
                actualVideoPos = checkPos;
                break;
              }
            }
          } catch (e) {
            continue;
          }
        }
        
        if (actualVideoPos === null) {
          return;
        }
        
        // Use the existing menu slot and group from the node view
        // No need to create or append - it's already in the DOM structure
        
        // Render React components after a delay to ensure DOM is stable
        // Use requestAnimationFrame to ensure ProseMirror has finished updating
        requestAnimationFrame(() => {
          setTimeout(() => {
            try {
              // Check if root already exists
              if (group._reactRoot) {
                // Re-render with current position
                const buttonContent = (
                  <>
                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        editor.chain().focus().setNodeSelection(actualVideoPos).run();
                        setTimeout(() => {
                          setMediaCaption(editor);
                        }, 10);
                      }}
                      tooltip="Toggle Caption"
                      data-style="ghost"
                      data-weight="small"
                    >
                      <ImageCaptionIcon className="tiptap-button-icon" />
                    </Button>
                    <Separator orientation="vertical" />
                    <Button
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        editor.chain().focus().setNodeSelection(actualVideoPos).run();
                        setTimeout(async () => {
                          await downloadSelectedMedia(editor);
                        }, 10);
                      }}
                      tooltip="Download"
                      data-style="ghost"
                      data-weight="small"
                    >
                      <ArrowDownToLineIcon className="tiptap-button-icon" />
                    </Button>
                    <Separator orientation="vertical" />
                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const rid = resolveResourceIdForVideoPos(editor, actualVideoPos);
                        if (!rid) return;
                        const ev = new CustomEvent('tiptap:resource:requestTags', { bubbles: true, cancelable: true, detail: { resourceId: rid } });
                        document.dispatchEvent(ev);
                      }}
                      tooltip="Tags"
                      data-style="ghost"
                      data-weight="small"
                    >
                      <TagIcon className="tiptap-button-icon" />
                    </Button>
                    <Separator orientation="vertical" />
                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        try {
                          const nodeDOM = editor.view.nodeDOM(actualVideoPos);
                          const ev = new CustomEvent('tiptap:video:requestReplace', {
                            bubbles: true,
                            cancelable: true,
                            detail: {
                              nodePos: actualVideoPos,
                              resourceId: resolveResourceIdForVideoPos(editor, actualVideoPos) || '',
                              src: editor.state.doc.nodeAt(actualVideoPos)?.attrs?.src || '',
                            }
                          });
                          if (nodeDOM) {
                            nodeDOM.dispatchEvent(ev);
                          }
                          if (!ev.defaultPrevented) {
                            document.dispatchEvent(ev);
                          }
                        } catch (err) {
                          console.error('[MediaNodeFloating] Failed to dispatch video replacement event:', err);
                        }
                      }}
                      tooltip="Replace Video"
                      data-style="ghost"
                      data-weight="small"
                    >
                      <RefreshCcwIcon className="tiptap-button-icon" />
                    </Button>
                    <Separator orientation="vertical" />
                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const rid = resolveResourceIdForVideoPos(editor, actualVideoPos);
                        if (rid) startThumbnailUpload(rid);
                      }}
                      tooltip="Upload thumbnail"
                      data-style="ghost"
                      data-weight="small"
                    >
                      <UploadThumbnailIcon className="tiptap-button-icon" />
                    </Button>
                    <Separator orientation="vertical" />
                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        editor.chain().focus().setNodeSelection(actualVideoPos).deleteSelection().run();
                      }}
                      tooltip="Delete"
                      data-style="ghost"
                      data-weight="small"
                    >
                      <TrashIcon className="tiptap-button-icon" />
                    </Button>
                  </>
                );
                group._reactRoot.render(buttonContent);
              } else {
                // Check if group still exists and is connected
                if (!group.isConnected) {
                  return;
                }
                
                const root = createRoot(group);
                
                const buttonContent = (
                  <>
                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        editor.chain().focus().setNodeSelection(actualVideoPos).run();
                        setTimeout(() => {
                          setMediaCaption(editor);
                        }, 10);
                      }}
                      tooltip="Toggle Caption"
                      data-style="ghost"
                      data-weight="small"
                    >
                      <ImageCaptionIcon className="tiptap-button-icon" />
                    </Button>
                    <Separator orientation="vertical" />
                    <Button
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        editor.chain().focus().setNodeSelection(actualVideoPos).run();
                        setTimeout(async () => {
                          await downloadSelectedMedia(editor);
                        }, 10);
                      }}
                      tooltip="Download"
                      data-style="ghost"
                      data-weight="small"
                    >
                      <ArrowDownToLineIcon className="tiptap-button-icon" />
                    </Button>
                    <Separator orientation="vertical" />
                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const rid = resolveResourceIdForVideoPos(editor, actualVideoPos);
                        if (rid) {
                          const ev = new CustomEvent('tiptap:resource:requestTags', { bubbles: true, cancelable: true, detail: { resourceId: rid } });
                          document.dispatchEvent(ev);
                        }
                      }}
                      tooltip="Tags"
                      data-style="ghost"
                      data-weight="small"
                    >
                      <TagIcon className="tiptap-button-icon" />
                    </Button>
                    <Separator orientation="vertical" />
                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        try {
                          const nodeDOM = editor.view.nodeDOM(actualVideoPos);
                          const ev = new CustomEvent('tiptap:video:requestReplace', {
                            bubbles: true,
                            cancelable: true,
                            detail: {
                              nodePos: actualVideoPos,
                              resourceId: resolveResourceIdForVideoPos(editor, actualVideoPos) || '',
                              src: editor.state.doc.nodeAt(actualVideoPos)?.attrs?.src || '',
                            }
                          });
                          if (nodeDOM) {
                            nodeDOM.dispatchEvent(ev);
                          }
                          if (!ev.defaultPrevented) {
                            document.dispatchEvent(ev);
                          }
                        } catch (err) {
                          console.error('[MediaNodeFloating] Failed to dispatch video replacement event:', err);
                        }
                      }}
                      tooltip="Replace Video"
                      data-style="ghost"
                      data-weight="small"
                    >
                      <RefreshCcwIcon className="tiptap-button-icon" />
                    </Button>
                    <Separator orientation="vertical" />
                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const rid = resolveResourceIdForVideoPos(editor, actualVideoPos);
                        if (rid) startThumbnailUpload(rid);
                      }}
                      tooltip="Upload thumbnail"
                      data-style="ghost"
                      data-weight="small"
                    >
                      <UploadThumbnailIcon className="tiptap-button-icon" />
                    </Button>
                    <Separator orientation="vertical" />
                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        editor.chain().focus().setNodeSelection(actualVideoPos).deleteSelection().run();
                      }}
                      tooltip="Delete"
                      data-style="ghost"
                      data-weight="small"
                    >
                      <TrashIcon className="tiptap-button-icon" />
                    </Button>
                  </>
                );
                
                root.render(buttonContent);
                // Store root on the group element
                group._reactRoot = root;
              }
            } catch (error) {
              console.error('[MediaNodeFloating] Error rendering React components:', error);
              // Fallback: add a simple visible button to verify menu is visible
              if (group && group.isConnected) {
                const fallbackBtn = document.createElement('button');
                fallbackBtn.textContent = 'Menu';
                fallbackBtn.style.cssText = 'padding: 4px 8px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 12px;';
                fallbackBtn.onclick = (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                };
                group.appendChild(fallbackBtn);
              }
            }
          }, 50);
        });
        
        console.log('[MediaNodeFloating] Menu populated for container', index, container);
        console.log('[MediaNodeFloating] Menu slot:', menuSlot, 'Menu innerHTML length:', menuSlot.innerHTML.length, 'Menu computed style:', window.getComputedStyle(menuSlot).display);
        
        // Check if buttons are actually rendered after a short delay
        setTimeout(() => {
          const buttons = menuSlot.querySelectorAll('button, [role="button"]');
          console.log('[MediaNodeFloating] Buttons found in menu:', buttons.length, 'Menu visible:', window.getComputedStyle(menuSlot).visibility, 'Menu opacity:', window.getComputedStyle(menuSlot).opacity, 'Menu width:', window.getComputedStyle(menuSlot).width, 'Menu height:', window.getComputedStyle(menuSlot).height);
          if (buttons.length === 0) {
            console.warn('[MediaNodeFloating] No buttons found in menu! Menu HTML:', menuSlot.innerHTML);
          }
        }, 100);
        
        // Helper function to find video position from container
        const findVideoPos = () => {
          const videoElement = container.querySelector('video');
          if (!videoElement) return null;
          const videoPos = posAtDOMSafe(editor, videoElement, 0);
          if (videoPos == null) return null;
          for (let checkPos = videoPos; checkPos >= 0; checkPos--) {
            try {
              const node = editor.state.doc.nodeAt(checkPos);
              if (node && node.type.name === 'video') {
                const nodeDOM = editor.view.nodeDOM(checkPos);
                if (nodeDOM === container || container.contains(nodeDOM)) {
                  return checkPos;
                }
              }
            } catch (e) {
              continue;
            }
          }
          return null;
        };
        
        // Set up MutationObserver to re-populate menu if React root is lost
        let isReattaching = false;
        const observer = new MutationObserver((mutations) => {
          // Prevent infinite loops
          if (isReattaching) return;
          
          // Check if menu slot exists but React content is missing
          const menuSlot = container.querySelector('.tiptap-video-fixed-menu[data-menu-slot="true"]');
          if (!menuSlot || !container.isConnected) return;
          
          const group = menuSlot.querySelector('.tiptap-toolbar-group[data-menu-group="true"]');
          if (!group) return;
          
          // If React root is missing, re-populate
          if (!group._reactRoot) {
            isReattaching = true;
            // Find current video position
            const currentVideoPos = findVideoPos();
            if (currentVideoPos === null) {
              isReattaching = false;
              return;
            }
            try {
              const root = createRoot(group);
              root.render(
                <>
                  <Button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const pos = findVideoPos();
                      if (pos !== null) {
                        editor.chain().focus().setNodeSelection(pos).run();
                        setTimeout(() => {
                          setMediaCaption(editor);
                        }, 10);
                      }
                    }}
                    tooltip="Toggle Caption"
                    data-style="ghost"
                    data-weight="small"
                  >
                    <ImageCaptionIcon className="tiptap-button-icon" />
                  </Button>
                  <Separator orientation="vertical" />
                  <Button
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const pos = findVideoPos();
                      if (pos !== null) {
                        editor.chain().focus().setNodeSelection(pos).run();
                        setTimeout(async () => {
                          await downloadSelectedMedia(editor);
                        }, 10);
                      }
                    }}
                    tooltip="Download"
                    data-style="ghost"
                    data-weight="small"
                  >
                    <ArrowDownToLineIcon className="tiptap-button-icon" />
                  </Button>
                  <Separator orientation="vertical" />
                  <Button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const pos = findVideoPos();
                      if (pos !== null) {
                        try {
                          const nodeDOM = editor.view.nodeDOM(pos);
                          const ev = new CustomEvent('tiptap:video:requestReplace', {
                            bubbles: true,
                            cancelable: true,
                            detail: {
                              nodePos: pos,
                              resourceId: editor.state.doc.nodeAt(pos)?.attrs?.resourceId || '',
                              src: editor.state.doc.nodeAt(pos)?.attrs?.src || '',
                            }
                          });
                          if (nodeDOM) {
                            nodeDOM.dispatchEvent(ev);
                          }
                          if (!ev.defaultPrevented) {
                            document.dispatchEvent(ev);
                          }
                        } catch (err) {
                          console.error('[MediaNodeFloating] Failed to dispatch video replacement event:', err);
                        }
                      }
                    }}
                    tooltip="Replace Video"
                    data-style="ghost"
                    data-weight="small"
                  >
                    <RefreshCcwIcon className="tiptap-button-icon" />
                  </Button>
                  <Separator orientation="vertical" />
                  <Button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const pos = findVideoPos();
                      const node = pos !== null ? editor.state.doc.nodeAt(pos) : null;
                      if (node?.attrs?.resourceId) startThumbnailUpload(node.attrs.resourceId);
                    }}
                    tooltip="Upload thumbnail"
                    data-style="ghost"
                    data-weight="small"
                  >
                    <UploadThumbnailIcon className="tiptap-button-icon" />
                  </Button>
                  <Separator orientation="vertical" />
                  <Button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const pos = findVideoPos();
                      if (pos !== null) {
                        editor.chain().focus().setNodeSelection(pos).deleteSelection().run();
                      }
                    }}
                    tooltip="Delete"
                    data-style="ghost"
                    data-weight="small"
                  >
                    <TrashIcon className="tiptap-button-icon" />
                  </Button>
                </>
              );
              group._reactRoot = root;
            } catch (err) {
              console.error('[MediaNodeFloating] Error re-rendering React components:', err);
            }
            // Reset flag after a short delay to allow DOM to settle
            setTimeout(() => {
              isReattaching = false;
            }, 100);
          }
        });
        
        // Observe the container for child removals
        observer.observe(container, {
          childList: true,
          subtree: false
        });
        
        // Store observer for cleanup on the menu slot
        if (menuSlot) {
          menuSlot._mutationObserver = observer;
        }
      });
    };

    // Find and attach initially - try multiple times to catch the placeholder
    attachTimeout = setTimeout(() => {
      findAndAttachMenus();
    }, 100);
    
    // Also try after a longer delay in case editor isn't fully ready
    setTimeout(() => {
      findAndAttachMenus();
    }, 500);

    // Watch for new video containers being added to the DOM
    let isProcessingNewContainers = false;
    let lastProcessTime = 0;
    const domObserver = new MutationObserver((mutations) => {
      // Prevent infinite loops
      if (isProcessingNewContainers) return;
      
      // Throttle: only process once per 200ms
      const now = Date.now();
      if (now - lastProcessTime < 200) return;
      
      let shouldReattach = false;
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            // Check if a new video container was added
            if (node.classList?.contains('video-extension-container')) {
              // Only reattach if it doesn't already have a menu
              if (!node.querySelector('.tiptap-video-fixed-menu')) {
                shouldReattach = true;
              }
            }
            // Or if a video container was added inside this node
            const containers = node.querySelectorAll?.('.video-extension-container');
            if (containers) {
              containers.forEach((container) => {
                if (!container.querySelector('.tiptap-video-fixed-menu')) {
                  shouldReattach = true;
                }
              });
            }
          }
        });
      });
      if (shouldReattach) {
        isProcessingNewContainers = true;
        lastProcessTime = Date.now();
        attachTimeout = setTimeout(() => {
          findAndAttachMenus();
          setTimeout(() => {
            isProcessingNewContainers = false;
          }, 100);
        }, 200);
      }
    });

    // Observe the editor DOM for new containers
    domObserver.observe(editorDOM, {
      childList: true,
      subtree: true
    });

    // Update on editor updates - debounced
    let updateTimeout = null;
    const handleUpdate = () => {
      if (updateTimeout) clearTimeout(updateTimeout);
      updateTimeout = setTimeout(() => {
        findAndAttachMenus();
        updateTimeout = null;
      }, 200);
    };

    editor.on('update', handleUpdate);
    editor.on('selectionUpdate', handleUpdate);
    editor.on('create', handleUpdate);

    return () => {
      // Clear timeouts
      if (attachTimeout) clearTimeout(attachTimeout);
      if (updateTimeout) clearTimeout(updateTimeout);
      
      // Disconnect DOM observer
      domObserver.disconnect();
      
      editor.off('update', handleUpdate);
      editor.off('selectionUpdate', handleUpdate);
      editor.off('create', handleUpdate);
      // Clean up menus and observers
      const dom = getEditorViewDom(editor);
      if (dom) {
        const menus = Array.from(dom.querySelectorAll('.tiptap-video-fixed-menu'));
        menus.forEach(menu => {
        // Disconnect MutationObserver if it exists
        if (menu._mutationObserver) {
          menu._mutationObserver.disconnect();
        }
        // Unmount React root if it exists
        if (menu._reactRoot) {
          menu._reactRoot.unmount();
        }
        menu.remove();
      });
      }
    };
  }, [editor]);

  // Listen for ResourceModal open/close events to hide floating menu
  useEffect(() => {
    const handleModalOpen = () => {
      setIsModalOpen(true);
      setHoveredRowElement(null);
      setHoveredRowType(null);
    };

    // Check if modal overlay exists in DOM
    const checkModal = () => {
      const modalOverlay = document.querySelector('.resource-modal-overlay');
      const modalIsOpen = !!modalOverlay;
      setIsModalOpen(prev => {
        // Only update if state actually changed to avoid unnecessary re-renders
        if (prev !== modalIsOpen) {
          return modalIsOpen;
        }
        return prev;
      });
    };

    // Listen for replacement events that open the modal
    document.addEventListener('tiptap:image:requestReplace', handleModalOpen);
    document.addEventListener('tiptap:video:requestReplace', handleModalOpen);
    document.addEventListener('tiptap:downloadRow:requestReplace', handleModalOpen);
    document.addEventListener('tiptap:externalLinkRow:requestReplace', handleModalOpen);
    document.addEventListener('tiptap:audioRow:requestReplace', handleModalOpen);

    // Check modal state periodically
    const checkModalInterval = setInterval(checkModal, 100);

    // Watch for DOM changes to detect modal open/close
    const observer = new MutationObserver(checkModal);
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Initial check
    checkModal();

    return () => {
      document.removeEventListener('tiptap:image:requestReplace', handleModalOpen);
      document.removeEventListener('tiptap:video:requestReplace', handleModalOpen);
      document.removeEventListener('tiptap:downloadRow:requestReplace', handleModalOpen);
      document.removeEventListener('tiptap:externalLinkRow:requestReplace', handleModalOpen);
      document.removeEventListener('tiptap:audioRow:requestReplace', handleModalOpen);
      clearInterval(checkModalInterval);
      observer.disconnect();
    };
  }, []); // Empty dependency array - effect only runs once

  // Get the selected node's DOM element for positioning
  useEffect(() => {
    if (!editor) {
      setReferenceElement(null);
      return;
    }

    const updateReferenceElement = () => {
      if (!editor || editor.isDestroyed) {
        setReferenceElement(null);
        return;
      }
      try {
        const { selection } = editor.state;
        if (selection && !selection.empty && selection instanceof NodeSelection) {
          const nodeDOM = editor.view.nodeDOM(selection.from);
          // For images/videos, nodeDOM might be the figure element
          // For table rows, nodeDOM might be the tr element or a child
          if (nodeDOM && nodeDOM instanceof HTMLElement) {
            // For table rows, find the tr element
            if (nodeDOM.tagName === 'TR' && nodeDOM.hasAttribute('data-resource-row')) {
              setReferenceElement(nodeDOM);
              activeRowTrRef.current = nodeDOM;
            } else {
              const tr = nodeDOM.closest('tr[data-resource-row]');
              if (tr) {
                setReferenceElement(tr);
                activeRowTrRef.current = tr;
              } else if (nodeDOM.closest('.tiptap-image')) {
                setReferenceElement(postEditorImageFloatingReference(nodeDOM));
              } else {
                setReferenceElement(nodeDOM);
              }
            }
          } else {
            setReferenceElement(null);
          }
        } else {
          setReferenceElement(null);
        }
      } catch {
        setReferenceElement(null);
      }
    };

    // Update immediately
    updateReferenceElement();

    // Also listen for selection updates
    editor.on('selectionUpdate', updateReferenceElement);
    editor.on('update', updateReferenceElement);

    return () => {
      editor.off('selectionUpdate', updateReferenceElement);
      editor.off('update', updateReferenceElement);
    };
  }, [editor, isImageSelected, isVideoSelected, isDownloadRowSelected, isExternalLinkRowSelected, isMeetingRowSelected, isAudioRowSelected]);

  // Removed hover listeners - videos now have fixed menus in their containers

  // Add click listeners for resource row elements (downloadRow, externalLinkRow, meetingRow)
  // Changed from hover to click for download and external link rows
  useEffect(() => {
    if (!editor) return;

    const dom = getEditorViewDom(editor);
    if (!dom) return;
    
    const handleRowClick = (e) => {
      // Don't handle clicks on the floating menu itself
      const floatingMenu = floatingRef.current;
      if (floatingMenu && floatingMenu.contains(e.target)) {
        return;
      }
      
      // Find the table row element
      const target = e.target;
      let tr = null;
      
      // Check if target itself is a tr
      if (target instanceof HTMLElement && target.tagName === 'TR' && target.hasAttribute('data-resource-row')) {
        tr = target;
      } else {
        // Otherwise, find the closest tr
        let current = target;
        let depth = 0;
        while (current && current instanceof HTMLElement && depth < 5) {
          if (current.tagName === 'TR' && current.hasAttribute('data-resource-row')) {
            tr = current;
            break;
          }
          current = current.parentElement;
          depth++;
        }
      }
      
      if (!tr || !(tr instanceof HTMLElement)) {
        // Clicked outside a row - hide the menu
        setHoveredRowElement(null);
        setHoveredRowType(null);
        return;
      }

      // Track the last row the user interacted with, regardless of selection behavior.
      activeRowTrRef.current = tr;
      
      // Determine the row type from the row's data attribute first (most reliable)
      const rowTypeAttr = tr.getAttribute('data-row-type');
      let detectedType = null;
      
      if (rowTypeAttr === 'download') {
        detectedType = 'downloadRow';
      } else if (rowTypeAttr === 'externalLink') {
        detectedType = 'externalLinkRow';
      } else if (rowTypeAttr === 'meeting') {
        detectedType = 'meetingRow';
      } else if (rowTypeAttr === 'audio') {
        detectedType = 'audioRow';
      }
      
      // If we couldn't get it from the attribute, try to find the node by walking through all row nodes
      if (!detectedType) {
        // Walk through the document to find which node corresponds to this tr
        for (let pos = 0; pos < editor.state.doc.content.size; pos++) {
          const node = editor.state.doc.nodeAt(pos);
          if (node && (node.type.name === 'downloadRow' || node.type.name === 'externalLinkRow' || node.type.name === 'meetingRow' || node.type.name === 'audioRow')) {
            const nodeDOM = editor.view.nodeDOM(pos);
            // Check if this node's DOM contains or is the tr we clicked
            if (nodeDOM) {
              let trFromNode = null;
              if (nodeDOM === tr) {
                trFromNode = tr;
              } else if (nodeDOM instanceof HTMLElement) {
                trFromNode = nodeDOM.closest('tr[data-resource-row]');
              }
              
              if (trFromNode === tr) {
                detectedType = node.type.name;
                break;
              }
            }
          }
        }
      }
      
      if (detectedType && (detectedType === 'downloadRow' || detectedType === 'externalLinkRow' || detectedType === 'meetingRow' || detectedType === 'audioRow')) {
        // If the clicked row is already node-selected, let selection updates handle it.
        const { selection } = editor.state;
        if (selection instanceof NodeSelection) {
          const selectedNode = editor.state.doc.nodeAt(selection.from);
          if (selectedNode && (selectedNode.type.name === 'downloadRow' || selectedNode.type.name === 'externalLinkRow' || selectedNode.type.name === 'meetingRow' || selectedNode.type.name === 'audioRow')) {
            // Only short-circuit if the selected row is the same row we clicked.
            if (rowEditorDomMatchesTr(editor, tr, selection.from)) {
              return;
            }
          }
        }
        
        // Toggle menu: if clicking the same row, hide it; otherwise show it
        if (hoveredRowElement === tr) {
          setHoveredRowElement(null);
          setHoveredRowType(null);
        } else {
          setHoveredRowElement(tr);
          setHoveredRowType(detectedType);
          
          // Debug logging
          if (process.env.NODE_ENV === 'development') {
            console.log('[MediaNodeFloating] Row clicked, showing menu:', {
              rowTypeAttr,
              detectedType,
              tr: tr.tagName,
              trDataRowType: tr.getAttribute('data-row-type')
            });
          }
        }
      } else {
        // Clicked on a row that's not one of our types, hide menu
        setHoveredRowElement(null);
        setHoveredRowType(null);
      }
    };

    // Also handle clicks outside to close the menu
    const handleDocumentClick = (e) => {
      const floatingMenu = floatingRef.current;
      // If clicking on the menu itself, don't close
      if (floatingMenu && floatingMenu.contains(e.target)) {
        return;
      }
      
      // If clicking on a row, handleRowClick will handle it
      const tr = e.target.closest('tr[data-resource-row]');
      if (tr) {
        return; // Let handleRowClick handle it
      }
      
      // Clicked outside any row - hide the menu
      setHoveredRowElement(null);
      setHoveredRowType(null);
    };

    dom.addEventListener('click', handleRowClick, true);
    document.addEventListener('click', handleDocumentClick, true);

    return () => {
      dom.removeEventListener('click', handleRowClick, true);
      document.removeEventListener('click', handleDocumentClick, true);
    };
  }, [editor, hoveredRowElement]);

  // Show floating menu if:
  // 1. Image is selected, OR
  // 2. Video is selected (for floating-ui positioning), OR
  // 3. Resource row is selected (downloadRow, externalLinkRow, meetingRow, audioRow), OR
  // 4. Resource row is being hovered (and not selected)
  // Note: Videos also have fixed menus in their containers (handled separately)
  const isRowSelected = isDownloadRowSelected || isExternalLinkRowSelected || isMeetingRowSelected || isAudioRowSelected;
  const isRowHovered = hoveredRowElement && !isRowSelected;
  
  const visible = (
    (isImageSelected && referenceElement) ||
    (isVideoSelected && referenceElement) ||
    (isRowSelected && referenceElement) ||
    isRowHovered
  ) && !shouldHide && !isModalOpen;
  
  // Determine which element to use as reference for positioning
  const positioningReference = isImageSelected || isVideoSelected || isRowSelected
    ? referenceElement 
    : hoveredRowElement;

  // Determine positioning strategy based on node type
  const isRowType = isRowSelected || hoveredRowElement;
  
  // Get the row element for positioning (defined early for use in hooks)
  const rowElement = hoveredRowElement || (referenceElement?.closest ? referenceElement.closest('tr[data-resource-row]') : null);
  
  // Use floating-ui for positioning (for both media and rows)
  // For rows, use the title cell (second td) as reference to match image positioning
  // For media, use positioningReference
  const rowReferenceElement =
    isRowType && rowElement ? postEditorResourceRowFloatingReference(rowElement) : null;
  const mediaReferenceElement = !isRowType
    ? isImageSelected && positioningReference
      ? postEditorImageFloatingReference(positioningReference)
      : positioningReference
    : null;
  const finalReferenceElement = rowReferenceElement || mediaReferenceElement;

  // Images and rows: top-end over the media/title box (not centered on full-width wrapper).
  const placement =
    isRowType || isImageSelected ? POST_EDITOR_BUBBLE_MENU_PLACEMENT : 'top';

  const { refs, floatingStyles, update } = useFloating({
    elements: {
      reference: finalReferenceElement,
      floating: floatingRef.current,
    },
    placement,
    // Match main: same middleware for images and rows (mainAxis: false keeps menu above).
    middleware: [
      offset(POST_EDITOR_BUBBLE_MENU_OFFSET),
      flip({ mainAxis: false, crossAxis: true }),
      shift({ padding: 5 }),
    ],
    strategy: 'absolute',
  });

  const adjustedFloatingStyles = React.useMemo(() => {
    if (!isRowType && !isImageSelected) return floatingStyles;
    return postEditorBubbleMenuFloatingStyles(floatingStyles);
  }, [floatingStyles, isRowType, isImageSelected]);

  // Update floating position when selection changes (but not on scroll)
  useEffect(() => {
    if (!visible || !update || !finalReferenceElement) return;
    
    // Update position immediately when selection changes
    update();
    
    // Only update on resize, not on scroll
    const handleResize = () => {
      if (visible && finalReferenceElement) {
        update();
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [visible, update, finalReferenceElement]);

  // Calculate video menus BEFORE any early returns
  // Helper to find video node position from container element
  const findVideoPosFromContainer = (container) => {
    if (!editor || !container) {
      return null;
    }
    
    try {
      // Try to find the video element inside the container first
      const videoElement = container.querySelector('video');
      if (!videoElement) {
        return null;
      }
      
      const pos =
        posAtDOMSafe(editor, videoElement, 0) ?? posAtDOMSafe(editor, container, 0);
      
      if (pos == null || pos < 0 || pos > editor.state.doc.content.size) {
        // Try alternative: walk through all nodes to find the one that matches this container
        const doc = editor.state.doc;
        for (let checkPos = 0; checkPos < doc.content.size; checkPos++) {
          try {
            const node = doc.nodeAt(checkPos);
            if (node && node.type.name === 'video') {
              const nodeDOM = editor.view.nodeDOM(checkPos);
              if (nodeDOM === container || (nodeDOM instanceof HTMLElement && container.contains(nodeDOM))) {
                return checkPos;
              }
            }
          } catch (e) {
            continue;
          }
        }
        return null;
      }
      
      // Search backwards first
      for (let checkPos = pos; checkPos >= 0; checkPos--) {
        try {
          const node = editor.state.doc.nodeAt(checkPos);
          if (node && node.type.name === 'video') {
            const nodeDOM = editor.view.nodeDOM(checkPos);
            if (nodeDOM && (nodeDOM === container || container.contains(nodeDOM) || (nodeDOM instanceof HTMLElement && nodeDOM.closest('.video-extension-container') === container))) {
              return checkPos;
            }
          }
        } catch (e) {
          // Skip invalid positions
          continue;
        }
      }
      
      // Search forward
      for (let checkPos = pos; checkPos < editor.state.doc.content.size; checkPos++) {
        try {
          const node = editor.state.doc.nodeAt(checkPos);
          if (node && node.type.name === 'video') {
            const nodeDOM = editor.view.nodeDOM(checkPos);
            if (nodeDOM && (nodeDOM === container || container.contains(nodeDOM) || (nodeDOM instanceof HTMLElement && nodeDOM.closest('.video-extension-container') === container))) {
              return checkPos;
            }
          }
        } catch (e) {
          // Skip invalid positions
          continue;
        }
      }
    } catch (e) {
      console.warn('[MediaNodeFloating] Error finding video position:', e);
    }
    
    return null;
  };

  // Render fixed menus inside each video container
  // Use a stable portal target (editor DOM) and position absolutely
  const videoMenus = videoContainers.map((container, index) => {
    if (!container || !(container instanceof HTMLElement)) {
      return null;
    }
    
    // Ensure container has position: relative for absolute positioning
    const computedStyle = window.getComputedStyle(container);
    if (computedStyle.position === 'static') {
      container.style.position = 'relative';
    }
    
    const videoPos = findVideoPosFromContainer(container);
    if (videoPos === null) {
      return null;
    }
    
    let videoNode = null;
    try {
      videoNode = editor.state.doc.nodeAt(videoPos);
      if (!videoNode || videoNode.type.name !== 'video') {
        return null;
      }
    } catch (e) {
      return null;
    }
    
    // Render menu directly inside container with absolute positioning
    // Use portal to render inside container (container has position: relative)
    
    const menuElement = (
      <div
        key={`video-menu-${index}`}
        className={postEditorVideoFixedMenuClasses()}
        data-floating-kind={POST_EDITOR_FLOATING_MENU_KINDS.video}
        onClick={(e) => {
          // Prevent clicks from propagating to video
          e.preventDefault();
          e.stopPropagation();
        }}
        onMouseDown={(e) => {
          // Prevent video controls from interfering
          e.stopPropagation();
        }}
      >
        <div {...postEditorFloatingToolbarProps(POST_EDITOR_FLOATING_MENU_KINDS.video)}>
          <div role="group" className="tiptap-toolbar-group tiptap-toolbar-group--video">
            <Button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // Select video first, then toggle caption
                if (videoPos !== null) {
                  editor.chain().focus().setNodeSelection(videoPos).run();
                  setTimeout(() => {
                    setMediaCaption(editor);
                  }, 10);
                }
              }}
              tooltip="Toggle Caption"
              data-style="ghost"
              data-weight="small"
            >
              <ImageCaptionIcon className="tiptap-button-icon" />
            </Button>
            <Separator orientation="vertical" className="tiptap-video-fixed-menu__separator" />
            <Button
              onClick={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                // Select video first, then download
                if (videoPos !== null) {
                  editor.chain().focus().setNodeSelection(videoPos).run();
                  setTimeout(async () => {
                    await downloadSelectedMedia(editor);
                  }, 10);
                }
              }}
              tooltip="Download"
              data-style="ghost"
              data-weight="small"
            >
              <ArrowDownToLineIcon className="tiptap-button-icon" />
            </Button>
            <Separator orientation="vertical" className="tiptap-video-fixed-menu__separator" />
            <Button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // Dispatch replacement event
                if (videoPos !== null && videoNode) {
                  try {
                    const nodeDOM = editor.view.nodeDOM(videoPos);
                    const eventName = 'tiptap:video:requestReplace';
                    const ev = new CustomEvent(eventName, {
                      bubbles: true,
                      cancelable: true,
                      detail: {
                        nodePos: videoPos,
                        resourceId: videoNode.attrs?.resourceId || '',
                        src: videoNode.attrs?.src || '',
                      }
                    });
                    if (nodeDOM) {
                      nodeDOM.dispatchEvent(ev);
                    }
                    if (!ev.defaultPrevented) {
                      document.dispatchEvent(ev);
                    }
                  } catch (err) {
                    console.error('[MediaNodeFloating] Failed to dispatch video replacement event:', err);
                  }
                }
              }}
              tooltip="Replace Video"
              data-style="ghost"
              data-weight="small"
            >
              <RefreshCcwIcon className="tiptap-button-icon" />
            </Button>
            <Separator orientation="vertical" className="tiptap-video-fixed-menu__separator" />
            <Button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (videoNode?.attrs?.resourceId) startThumbnailUpload(videoNode.attrs.resourceId);
              }}
              tooltip="Upload thumbnail"
              data-style="ghost"
              data-weight="small"
            >
              <UploadThumbnailIcon className="tiptap-button-icon" />
            </Button>
            <Separator orientation="vertical" className="tiptap-video-fixed-menu__separator" />
            <Button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // Select and delete video
                if (videoPos !== null) {
                  editor.chain().focus().setNodeSelection(videoPos).deleteSelection().run();
                }
              }}
              tooltip="Delete"
              data-style="ghost"
              data-weight="small"
            >
              <TrashIcon className="tiptap-button-icon" />
            </Button>
          </div>
        </div>
      </div>
    );
    
    const isUploadingThumbnail = thumbnailUploadingResourceId === String(videoNode.attrs?.resourceId ?? '');
    const thumbnailOverlay = isUploadingThumbnail ? (
      <div className="tiptap-video-thumbnail-overlay" aria-label="Uploading thumbnail">
        <div className="tiptap-video-thumbnail-overlay__spinner" />
      </div>
    ) : null;

    // Use portal to render overlay + menu inside container
    const portal = createPortal(
      <>
        {thumbnailOverlay}
        {menuElement}
      </>,
      container
    );
    return portal;
  }).filter(Boolean);

  if (!editor) {
    // Still return video menus even if editor check fails (they're already calculated)
    return <>{videoMenus}</>;
  }

  // For images, wrap ImageNodeFloating with positioning
  if (isImageSelected) {
    return (
      <>
        {videoMenus}
        <div
          ref={(node) => {
            floatingRef.current = node;
            refs.setFloating(node);
          }}
          tabIndex={-1}
          data-floating-ui-focusable=""
          className={postEditorFloatingContainerClasses(POST_EDITOR_FLOATING_MENU_KINDS.image)}
          data-floating-kind={POST_EDITOR_FLOATING_MENU_KINDS.image}
          style={postEditorFloatingPositionStyle(adjustedFloatingStyles)}
        >
          <ImageNodeFloating editor={editor} embedded floatingKind={POST_EDITOR_FLOATING_MENU_KINDS.image} />
        </div>
      </>
    );
  }

  // For resource rows (downloadRow, externalLinkRow, meetingRow, audioRow)
  let rowMenuContent = null;
  if (isRowSelected || hoveredRowElement) {
    // Row type: when a resource row is node-selected, that wins over stale click-to-hover
    // state (otherwise the toolbar can show the previous row while selection moved).
    let currentRowType = null;
    const { selection } = editor.state;
    if (isRowSelected && selection instanceof NodeSelection) {
      const node = editor.state.doc.nodeAt(selection.from);
      if (node && (node.type.name === 'downloadRow' || node.type.name === 'externalLinkRow' || node.type.name === 'meetingRow' || node.type.name === 'audioRow')) {
        currentRowType = node.type.name;
      }
    }
    if (!currentRowType) {
      currentRowType = hoveredRowType;
    }

    // If still no type, try to get from the row element itself
    if (!currentRowType) {
      const tr = hoveredRowElement || (referenceElement?.closest ? referenceElement.closest('tr[data-resource-row]') : referenceElement);
      if (tr && tr instanceof HTMLElement) {
        const rowTypeAttr = tr.getAttribute('data-row-type');
        if (rowTypeAttr === 'download' || rowTypeAttr === 'externalLink' || rowTypeAttr === 'meeting' || rowTypeAttr === 'audio') {
          currentRowType = rowTypeAttr === 'download' ? 'downloadRow' : 
                          rowTypeAttr === 'externalLink' ? 'externalLinkRow' : 
                          rowTypeAttr === 'meeting' ? 'meetingRow' : 
                          rowTypeAttr === 'audio' ? 'audioRow' : null;
        } else {
          const rowStartPos = resolveRowPosForResourceTr(editor, tr, null);
          if (rowStartPos != null) {
            const node = editor.state.doc.nodeAt(rowStartPos);
            if (node && ROW_RESOURCE_NODE_TYPES.includes(node.type.name)) {
              currentRowType = node.type.name;
            }
          }
        }
      }
    }
    
    const handleReplaceAction = (e) => {
      // Close floating menu immediately when Replace is clicked (so it doesn't stay open over the modal)
      setHoveredRowElement(null);
      setHoveredRowType(null);
      setIsModalOpen(true);

      if (!editor) {
        console.warn('[MediaNodeFloating] No editor available');
        return;
      }

      // Prevent default to avoid navigation
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      
      // Find the row element - try hoveredRowElement first, then referenceElement
      let tr = hoveredRowElement;
      if (!tr && referenceElement) {
        if (referenceElement instanceof HTMLElement) {
          tr = referenceElement.closest('tr[data-resource-row]');
        }
      }
      
      if (!tr || !(tr instanceof HTMLElement)) {
        console.warn('[MediaNodeFloating] No valid tr element found');
        return;
      }
      
      const rowPos = resolveRowPosForResourceTr(editor, tr, currentRowType || null);
      if (rowPos == null) {
        console.warn('[MediaNodeFloating] Could not resolve row document position');
        return;
      }

      const rowNode = editor.state.doc.nodeAt(rowPos);
      if (!rowNode || !ROW_RESOURCE_NODE_TYPES.includes(rowNode.type.name)) {
        console.warn('[MediaNodeFloating] Could not find rowNode');
        return;
      }

      const nodeType = currentRowType || rowNode.type.name;
      
      if (nodeType === 'downloadRow') {
        // Dispatch replace event for downloadRow (same as drag context menu)
        try {
          const ev = new CustomEvent('tiptap:downloadRow:requestReplace', {
            bubbles: true,
            detail: {
              href: `${rowNode.attrs.href || ''}`,
              filename: `${rowNode.attrs.filename || ''}`,
              contentType: `${rowNode.attrs.contentType || ''}`,
              signedId: `${rowNode.attrs.signedId || ''}`
            }
          });
          tr.dispatchEvent(ev);
        } catch (err) {
          console.error('Failed to trigger file upload for downloadRow:', err);
        }
      } else if (nodeType === 'externalLinkRow') {
        // Dispatch replace event for externalLinkRow (same as drag context menu)
        try {
          // Dispatch directly on document (handler listens on document)
          const docEv = new CustomEvent('tiptap:externalLinkRow:requestReplace', {
            bubbles: true,
            cancelable: true,
            detail: {
              resourceId: `${rowNode.attrs.resourceId || ''}`,
              href: `${rowNode.attrs.href || ''}`,
              trElement: tr // Include tr element in detail for handler
            }
          });
          
          document.dispatchEvent(docEv);
          
          // Also dispatch from tr element for consistency
          const trEv = new CustomEvent('tiptap:externalLinkRow:requestReplace', {
            bubbles: true,
            cancelable: true,
            detail: {
              resourceId: `${rowNode.attrs.resourceId || ''}`,
              href: `${rowNode.attrs.href || ''}`,
              trElement: tr
            }
          });
          tr.dispatchEvent(trEv);
        } catch (err) {
          console.error('[MediaNodeFloating] Failed to trigger externalLinkRow replace:', err);
        }
      } else if (nodeType === 'audioRow') {
        // Dispatch replace event for audioRow (same as double-click on row)
        try {
          const ev = new CustomEvent('tiptap:audioRow:requestReplace', {
            bubbles: true,
            detail: { resourceId: `${rowNode.attrs.resourceId || ''}` }
          });
          tr.dispatchEvent(ev);
        } catch (err) {
          console.error('Failed to trigger replace for audioRow:', err);
        }
      } else if (nodeType === 'meetingRow') {
        // For meetingRow, use prompt (same as drag context menu)
        const prevUrl = rowNode.attrs?.href || "https://";
        const url = window.prompt("Edit URL:", prevUrl);
        if (url === null) return;
        editor.chain().focus().setNodeSelection(rowPos).updateAttributes('meetingRow', { href: url.trim() !== '' ? url.trim() : null }).run();
      }
    };

    const rowKind = postEditorFloatingKindFromRowType(currentRowType);

    // Create the menu content
    const menuContent = (
      <div
        ref={(node) => {
          floatingRef.current = node;
          refs.setFloating(node);
        }}
        tabIndex={-1}
        data-floating-ui-focusable=""
        className={postEditorFloatingContainerClasses(rowKind)}
        data-floating-kind={rowKind || undefined}
        style={postEditorFloatingPositionStyle(isRowType ? adjustedFloatingStyles : floatingStyles)}
      >
        <div {...postEditorFloatingToolbarProps(rowKind)}>
          <div
            role="group"
            className={postEditorFloatingToolbarGroupClasses(rowKind)}
          >
            {(() => {
              // Ensure we have a valid row type
              if (!currentRowType) return null;

              const resolveRowNode = () => {
                const { selection } = editor.state;
                if (selection instanceof NodeSelection) {
                  const selected = editor.state.doc.nodeAt(selection.from);
                  if (
                    selected &&
                    ROW_RESOURCE_NODE_TYPES.includes(selected.type.name) &&
                    (!currentRowType || selected.type.name === currentRowType)
                  ) {
                    return selected;
                  }
                }

                const $from = selection.$from;
                for (let d = $from.depth; d >= 0; d -= 1) {
                  const n = $from.node(d);
                  if (!n || !ROW_RESOURCE_NODE_TYPES.includes(n.type.name)) continue;
                  if (currentRowType && n.type.name !== currentRowType) continue;
                  return n;
                }

                const tr =
                  hoveredRowElement ||
                  (referenceElement?.closest ? referenceElement.closest('tr[data-resource-row]') : referenceElement);
                if (!tr || !(tr instanceof HTMLElement)) return null;

                const rowPos = resolveRowPosForResourceTr(editor, tr, currentRowType);
                if (typeof rowPos !== 'number') return null;
                const node = editor.state.doc.nodeAt(rowPos);
                return node && ROW_RESOURCE_NODE_TYPES.includes(node.type.name) ? node : null;
              };

              const rowNode = resolveRowNode();
              const canAddSubtitle = !rowNode || !resourceRowHasSubtitle(rowNode);
              const subtitleButton = canAddSubtitle ? (
                  <>
                    <Button
                      onMouseDown={(e) => {
                        e.preventDefault();
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const trEl =
                          activeRowTrRef.current ||
                          hoveredRowElement ||
                          (referenceElement?.closest
                            ? referenceElement.closest('tr[data-resource-row]')
                            : null);
                        let rowPos = resolveRowPosForResourceTr(
                          editor,
                          trEl,
                          currentRowType || null
                        );
                        if (rowPos == null) {
                          const rowDepth = findResourceRowDepth(
                            editor.state.selection.$from
                          );
                          if (rowDepth >= 0) {
                            rowPos = editor.state.selection.$from.before(rowDepth);
                          }
                        }
                        if (rowPos != null) {
                          editor.commands.addResourceRowSubtitle(rowPos);
                        }
                      }}
                      tooltip="Add Subtitle"
                      data-style="ghost"
                      data-weight="small"
                    >
                      <PlusIcon className="tiptap-button-icon" />
                    </Button>
                    <Separator orientation="vertical" />
                  </>
                ) : null;
              
              // Helper to handle delete - selects node first, then deletes, then closes floating menu
              const handleDelete = (e) => {
                e.preventDefault();
                e.stopPropagation();

                const tr = hoveredRowElement || (referenceElement?.closest ? referenceElement.closest('tr[data-resource-row]') : referenceElement);
                if (!tr || !(tr instanceof HTMLElement)) return;

                const rowStartPos = resolveRowPosForResourceTr(editor, tr, currentRowType || null);
                if (rowStartPos == null) return;

                editor.chain().focus().setNodeSelection(rowStartPos).deleteSelection().run();
                setHoveredRowElement(null);
                setHoveredRowType(null);
              };

              const openTags = (e) => {
                e.preventDefault();
                e.stopPropagation();
                let rid = resolveResourceIdForToolbarRow(editor, currentRowType);
                if (!rid) {
                  const tr =
                    activeRowTrRef.current ||
                    hoveredRowElement ||
                    (referenceElement?.closest ? referenceElement.closest('tr[data-resource-row]') : referenceElement);
                  rid = resolveResourceIdForResourceRow(editor, tr);
                }
                if (!rid) {
                  // eslint-disable-next-line no-console
                  console.warn('[resource_tags] row has no resource id (DOM or doc)', { rowType: currentRowType });
                  return;
                }
                const ev = new CustomEvent('tiptap:resource:requestTags', { bubbles: true, cancelable: true, detail: { resourceId: rid } });
                document.dispatchEvent(ev);
              };
              
              if (currentRowType === 'downloadRow') {
                return (
                  <>
                    {subtitleButton}
                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleReplaceAction(e);
                      }}
                      tooltip="Replace File"
                      data-style="ghost"
                      data-weight="small"
                    >
                      <RefreshCcwIcon className="tiptap-button-icon" />
                    </Button>
                    <Separator orientation="vertical" />
                    <Button onClick={openTags} tooltip="Tags" data-style="ghost" data-weight="small">
                      <TagIcon className="tiptap-button-icon" />
                    </Button>
                    <Separator orientation="vertical" />
                    <Button
                      onClick={handleDelete}
                      tooltip="Delete"
                      data-style="ghost"
                      data-weight="small"
                    >
                      <TrashIcon className="tiptap-button-icon" />
                    </Button>
                  </>
                );
              } else if (currentRowType === 'audioRow') {
                return (
                  <>
                    {subtitleButton}
                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleReplaceAction(e);
                      }}
                      tooltip="Replace Audio"
                      data-style="ghost"
                      data-weight="small"
                    >
                      <RefreshCcwIcon className="tiptap-button-icon" />
                    </Button>
                    <Separator orientation="vertical" />
                    <Button onClick={openTags} tooltip="Tags" data-style="ghost" data-weight="small">
                      <TagIcon className="tiptap-button-icon" />
                    </Button>
                    <Separator orientation="vertical" />
                    <Button onClick={handleDelete} tooltip="Delete" data-style="ghost" data-weight="small">
                      <TrashIcon className="tiptap-button-icon" />
                    </Button>
                  </>
                );
              } else if (currentRowType === 'externalLinkRow') {
                return (
                  <>
                    {subtitleButton}
                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleReplaceAction(e);
                      }}
                      tooltip="Replace URL"
                      data-style="ghost"
                      data-weight="small"
                    >
                      <LinkIcon className="tiptap-button-icon" />
                    </Button>
                    <Separator orientation="vertical" />
                    <Button onClick={openTags} tooltip="Tags" data-style="ghost" data-weight="small">
                      <TagIcon className="tiptap-button-icon" />
                    </Button>
                    <Separator orientation="vertical" />
                    <Button
                      onClick={handleDelete}
                      tooltip="Delete"
                      data-style="ghost"
                      data-weight="small"
                    >
                      <TrashIcon className="tiptap-button-icon" />
                    </Button>
                  </>
                );
              } else if (currentRowType === 'meetingRow') {
                return (
                  <>
                    {subtitleButton}
                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleReplaceAction(e);
                      }}
                      tooltip="Edit URL"
                      data-style="ghost"
                      data-weight="small"
                    >
                      <LinkIcon className="tiptap-button-icon" />
                    </Button>
                    <Separator orientation="vertical" />
                    <Button
                      onClick={handleDelete}
                      tooltip="Delete"
                      data-style="ghost"
                      data-weight="small"
                    >
                      <TrashIcon className="tiptap-button-icon" />
                    </Button>
                  </>
                );
              }
              return null;
            })()}
          </div>
        </div>
      </div>
    );
    
    // For rows, render in a portal to the editor's DOM container (not inside the table)
    // For media (images/videos), render normally
    const portalRoot = getEditorViewDom(editor)?.parentElement || document.body;
    if (isRowType && portalRoot) {
      rowMenuContent = createPortal(menuContent, portalRoot);
    } else {
      rowMenuContent = menuContent;
    }
  }

  // Return all video menus plus other floating menus
  // Video menus are always rendered (fixed in containers), other menus depend on visible state
  return (
    <>
      <input
        ref={thumbnailFileInputRef}
        type="file"
        accept="image/*,image/svg+xml"
        aria-hidden="true"
        tabIndex={-1}
        className="tiptap-floating-menu__sr-only"
        onChange={handleThumbnailFileChange}
      />
      {videoMenus}
      {/* Keep existing floating menus for images and rows */}
      {visible && (
        <>
          {isImageSelected && (
            <div
              ref={(node) => {
                floatingRef.current = node;
                refs.setFloating(node);
              }}
              tabIndex={-1}
              data-floating-ui-focusable=""
              className={postEditorFloatingContainerClasses(POST_EDITOR_FLOATING_MENU_KINDS.image)}
              data-floating-kind={POST_EDITOR_FLOATING_MENU_KINDS.image}
              style={postEditorFloatingPositionStyle(adjustedFloatingStyles)}
            >
              <ImageNodeFloating editor={editor} embedded floatingKind={POST_EDITOR_FLOATING_MENU_KINDS.image} />
            </div>
          )}
        </>
      )}
      {rowMenuContent}
    </>
  );
};
